/**
 * useChat — unified WebSocket + REST hook for all chat types.
 * Supports: text, image (base64), GPS request/update, read receipts, presence.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const wsUrl = (path) => {
  if (API) {
    const base = API.startsWith('https://') ? API.replace('https://', 'wss://') : API.replace('http://', 'ws://');
    return base + path;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
};

export function useChat({ type = 'tradelink', conversationId, currentUser }) {
  const [messages,     setMessages]     = useState([]);
  const [isConnected,  setIsConnected]  = useState(false);
  const [peerPresence, setPeerPresence] = useState({ status: 'offline', lastSeen: null, name: '' });
  const [gpsSession,   setGpsSession]   = useState(null);
  const wsRef   = useRef(null);
  const retryRef = useRef(null);

  const path = type === 'direct'
    ? `/ws/chat/direct/${conversationId}/`
    : `/ws/chat/tradelink/${conversationId}/`;

  const myId = currentUser?.id || currentUser?.pk;

  const connect = useCallback(() => {
    if (!conversationId || !currentUser) return;
    const token = localStorage.getItem('user_token');
    if (!token) return;

    const socket = new WebSocket(`${wsUrl(path)}?token=${token}`);
    wsRef.current = socket;

    socket.onopen = () => {
      setIsConnected(true);
      socket.send(JSON.stringify({ type: 'mark_read' }));
    };

    socket.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'chat_message') {
        setMessages(prev => {
          const exists = prev.some(m => m.id === data.id);
          if (exists) return prev;
          const newMsg = { ...data, status: data.sender === myId ? 'sent' : 'received' };
          return [...prev, newMsg];
        });
        if (data.sender !== myId && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'mark_read' }));
        }
        if (data.message_type === 'gps_request') {
          setGpsSession({ sessionId: data.gps_session_id, status: 'requested', requesterId: data.sender });
        }
        if (data.message_type === 'gps_update') {
          setGpsSession(prev => prev ? { ...prev, lat: data.gps_lat, lng: data.gps_lng, address: data.gps_address, status: 'active' } : null);
        }
      } else if (data.type === 'read_receipt') {
        if (data.reader_id !== myId)
          setMessages(prev => prev.map(m => m.sender === myId ? { ...m, status: 'read' } : m));
      } else if (data.type === 'presence') {
        if (data.user_id !== myId)
          setPeerPresence({ status: data.status, lastSeen: data.last_seen, name: data.user_name });
      }
    };

    socket.onerror  = () => setIsConnected(false);
    socket.onclose  = () => {
      setIsConnected(false);
      retryRef.current = setTimeout(connect, 4000);
    };
  }, [conversationId, currentUser, path, myId]);

  // Load history
  useEffect(() => {
    if (!conversationId) return;
    const token = localStorage.getItem('user_token');
    if (!token) return;
    const url = type === 'direct'
      ? `${API}/api/chat/direct/${conversationId}/history/`
      : `${API}/api/chat/tradelink/${conversationId}/history/`;
    fetch(url, { headers: { Authorization: `Token ${token}` } })
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setMessages(arr.map(m => ({ ...m, status: m.sender === myId ? 'read' : 'received' })));
      })
      .catch(console.error);
  }, [conversationId, type, myId]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(retryRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendText = useCallback((text) => {
    if (!text.trim()) return false;
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'message', message: text }));
      return true;
    }
    return false;
  }, []);

  const sendImage = useCallback((base64Data, fileName, caption = '') => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'image', image_data: base64Data, image_name: fileName, caption }));
      return true;
    }
    return false;
  }, []);

  const requestGPS = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'gps_request' }));
      return true;
    }
    return false;
  }, []);

  const sendGPSUpdate = useCallback((lat, lng, accuracy, address, sessionId) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'gps_update', lat, lng, accuracy, address, session_id: sessionId }));
      return true;
    }
    return false;
  }, []);

  return { messages, setMessages, isConnected, peerPresence, gpsSession, setGpsSession,
           sendText, sendImage, requestGPS, sendGPSUpdate };
}
